import { observer } from 'mobx-react-lite';
import { CommonLink } from '@client/component/CommonLink';
import { twMerge } from 'tailwind-merge';
import { ChevronRightIcon, ExternalLinkIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

/**
 * <Box> 내부에서 설정 링크로 사용하는 컴포넌트
 * - 마진이나 패딩 없이 편리하게 나열에서 쓸 수 있음
 * - 클릭 시 링크로 이동
 */
export const SettingLink = observer(
    ({
        linkTo,
        title,
        className,
        isExternalLink = false,
    }: {
        linkTo: string;
        title: string;
        className?: string;
        isExternalLink?: boolean;
    }) => {
        return (
            <CommonLink
                to={linkTo}
                className={twMerge(
                    'flex h-9.5 w-full items-center justify-between text-left',
                    className,
                )}
                target={isExternalLink ? '_blank' : undefined}
            >
                {title}
                {!isExternalLink && (
                    <ChevronRightIcon className="text-base-content/40 size-5 grow-0" />
                )}
                {isExternalLink && (
                    <ExternalLinkIcon className="text-base-content/40 size-4 grow-0 stroke-[1.5]" />
                )}
            </CommonLink>
        );
    },
);

/**
 * <Box> 내부에서 설정 버튼으로 사용하는 컴포넌트
 * - 마진이나 패딩 없이 편리하게 나열에서 쓸 수 있음
 * - 클릭 시 현재 값을 업데이트하고 클릭 이벤트 발생
 */
export const SettingButton = observer(
    ({
        className,
        title,
        onClick,
        getCurrentValue,
    }: {
        title: string;
        className?: string;
        onClick?: (updateCurrentValue: () => void) => void;
        /**
         * 현재 값이 있으면 표시 (우측에 표시됨)
         */
        getCurrentValue?: (() => string | null) | (() => Promise<string | null>);
    }) => {
        const [currentValue, setCurrentValue] = useState<string | null>(null);

        const updateCurrentValue = useCallback(() => {
            if (getCurrentValue) {
                const value = getCurrentValue();
                if (value instanceof Promise) {
                    value.then((value) => {
                        setCurrentValue(value);
                    });
                } else {
                    setCurrentValue(value);
                }
            }
        }, [getCurrentValue]);

        useEffect(() => {
            updateCurrentValue();
        }, [updateCurrentValue]);

        return (
            <button
                className={twMerge(
                    'flex h-9.5 w-full cursor-pointer items-center justify-between text-left',
                    className,
                )}
                onClick={(e) => {
                    e.preventDefault();
                    onClick?.(updateCurrentValue);
                }}
            >
                <span className="grow">{title}</span>
                {currentValue && (
                    <span className="text-base-content/60 text-sm">{currentValue}</span>
                )}
            </button>
        );
    },
);
